// src/components/marketing/AboutPage.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import logo from "../../images/logo.png";
import Config from "../../config/Config";

// Hardcoded list of outstanding contributor user IDs
const outstandingContributorIds = [
  1, // David Aaron Campos
  2,
  24,
  476,
];

// Custom contributor card component (name, picture, bio only)
const ContributorCard = ({ user, extended_user }) => {
  const {
    first_name = '',
    last_name = '',
    username = '',
  } = user || {};

  const {
    picture_link = '',
    bio = '',
  } = extended_user || {};

  const profileUrl = `/${username}`;

  return (
    <a 
      href={profileUrl}
      style={{
        textDecoration: "none",
        color: "inherit",
        display: "block",
        transition: "transform 0.2s ease, box-shadow 0.2s ease"
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.15)";
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div style={{
        border: "1px solid var(--border)",
        borderRadius: "12px",
        padding: "20px",
        background: "var(--surface)",
        textAlign: "center",
        cursor: "pointer"
      }}>
        {picture_link && (
          <img
            src={picture_link}
            alt={`${first_name} ${last_name} profile`}
            style={{
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              objectFit: "cover",
              margin: "0 auto 16px"
            }}
          />
        )}

        <h4 style={{ 
          margin: "0 0 12px 0", 
          fontWeight: "600",
          color: "var(--ink)"
        }}>
          {first_name} {last_name}
        </h4>

        {bio && (
          <p style={{ 
            fontSize: "14px", 
            color: "var(--muted)",
            lineHeight: "1.4",
            margin: "0"
          }}>
            {bio}
          </p>
        )}
      </div>
    </a>
  );
};

const AboutPage = () => {
  const [contributors, setContributors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchContributors = async () => {
      try {
        setLoading(true);
        const contributorPromises = outstandingContributorIds.map(async (userId) => {
          // Fetch both basic user data and extended user data
          const [userResponse, extendedUserResponse] = await Promise.all([
            axios.get(`${Config().userUrl}${userId}/`),
            axios.get(`${Config().extendedUserUrl}${userId}/`)
          ]);
          
          return {
            user: userResponse.data,
            extended_user: extendedUserResponse.data
          };
        });
        
        const contributorData = await Promise.all(contributorPromises);
        setContributors(contributorData);
      } catch (err) {
        console.error("About Page Contributors Fetch Error:", err);
        console.error("About Page Contributors Error Response:", err.response);
        console.error("About Page Contributors Error Status:", err.response?.status);
        console.error("About Page Contributors Error Data:", err.response?.data);
        setError("Unable to load contributor information.");
      } finally {
        setLoading(false);
      }
    };

    if (outstandingContributorIds.length > 0) {
      fetchContributors();
    } else {
      setLoading(false);
    }
  }, []);

  return (
    <div className="container" style={{ maxWidth: 900, padding: "48px 16px" }}>
      {/* Header */}
      <header style={{ textAlign: "center", marginBottom: 64 }}>
        <img
          src={logo}
          alt="Inertia7 logo"
          width={80}
          height={80}
          style={{ display: "inline-block" }}
        />
        <h1 style={{ marginTop: 24, marginBottom: 16 }}>About Inertia7</h1>
        <p style={{ color: "var(--muted, #64748b)", fontSize: "18px" }}>
          A platform for documenting your technical projects.
        </p>
      </header>

      {/* Why */}
      <section style={{ marginBottom: 48 }}>
        <h2 style={{ 
          color: "var(--brand, #59c6c1)", 
          marginTop: 48, 
          marginBottom: 24,
          fontSize: "28px"
        }}>Why</h2>
        <p>
          Most platforms for code and data projects are built for storage, not
          communication. Repositories bury the context in READMEs that nobody
          reads. Blogs are too detached from the actual work. Inertia7 exists
          because documenting technical work should be straightforward, tied to
          the source, and not feel like an afterthought.
        </p>
      </section>

      {/* What */}
      <section style={{ marginBottom: 48 }}>
        <h2 style={{ 
          color: "var(--brand, #59c6c1)", 
          marginTop: 48, 
          marginBottom: 24,
          fontSize: "28px"
        }}>What</h2>
        <p>
          Inertia7 is a project documentation platform. It supports code,
          visualizations, math notation, and plain language in one place. It’s
          designed for engineers, analysts, and researchers who want their
          work—data pipelines, experiments, tools, or models—explained in a way
          that someone competent can pick up and run with.
        </p>
      </section>

      {/* How */}
      <section style={{ marginBottom: 48 }}>
        <h2 style={{ 
          color: "var(--brand, #59c6c1)", 
          marginTop: 48, 
          marginBottom: 24,
          fontSize: "28px"
        }}>How</h2>
        <p>
          Front end: React with Markdown and LaTeX rendering.  
          Back end: Django (Python) with a PostgreSQL database.  
          The stack is kept minimal on purpose—fast to deploy, easy to maintain,
          and predictable to debug. The focus is on stable tooling and
          functionality, not chasing every new framework trend.
        </p>
      </section>

      {/* Outstanding Contributors */}
      <section>
        <h2 style={{ 
          color: "var(--brand, #59c6c1)", 
          marginTop: 48, 
          marginBottom: 24,
          fontSize: "28px"
        }}>Outstanding Contributors</h2>
        
        {loading && (
          <p style={{ color: "var(--muted)", fontStyle: "italic" }}>
            Loading contributors...
          </p>
        )}
        
        {error && (
          <p style={{ color: "var(--error, #dc3545)" }}>
            {error}
          </p>
        )}
        
        {!loading && !error && contributors.length === 0 && (
          <p style={{ color: "var(--muted)" }}>
            No outstanding contributors configured.
          </p>
        )}
        
        {!loading && !error && contributors.length > 0 && (
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", 
            gap: "16px", 
            marginTop: "16px" 
          }}>
            {contributors.map((contributor) => (
              <ContributorCard 
                key={contributor.user.pk || contributor.user.id} 
                user={contributor.user} 
                extended_user={contributor.extended_user}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default AboutPage;
